import { Icon, Text } from '@dixa/lib-shared-habla';
import { SearchUserEnriched } from 'lib-search-types';
import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import styles from 'components/presence/components/presenceList.module.scss';
import { PresenceListItem } from 'components/presence/components/presenceListItem';
import { PresenceSectionSkeleton } from 'components/presence/components/presenceSectionSkeleton';
import { PresenceSectionWarning } from 'components/presence/components/presenceSectionWarning';
import {
  getStatus,
  PresenceStatus,
  sortMembers,
} from 'components/presence/utils';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

type LegacyPresenceListSectionsProps = {
  members: SearchUserEnriched[];
  profile: User;
  awayStatuses: AwayStatus[];
  isLoading: boolean;
};

const statusOrder: PresenceStatus[] = ['working', 'away', 'offline'];

export const LegacyPresenceListSections = ({
  members,
  profile,
  awayStatuses,
  isLoading,
}: LegacyPresenceListSectionsProps): JSX.Element => {
  const { favoriteAgentIds, addFavorite, removeFavorite } = useFavoriteAgents();

  const groupedMembers = useMemo(() => {
    const sorted = sortMembers(members);
    const groups: Record<PresenceStatus, SearchUserEnriched[]> = {
      working: [],
      away: [],
      offline: [],
    };

    for (const member of sorted) {
      groups[getStatus(member)].push(member);
    }

    return statusOrder
      .map((status) => {
        const group = groups[status];
        const ownMember = group.find((member) => member.id === profile.id);
        const others = group.filter((member) => member.id !== profile.id);
        return {
          status,
          members: ownMember ? [ownMember, ...others] : others,
        };
      })
      .filter((group) => group.members.length > 0);
  }, [members, profile.id]);

  const isEmpty = members.length === 0;

  return isLoading ? (
    <>
      <PresenceSectionSkeleton />
      <PresenceSectionSkeleton />
      <PresenceSectionSkeleton />
    </>
  ) : isEmpty ? (
    <PresenceSectionWarning
      title="There's no one here yet"
      message={
        <>
          You can invite people by clicking on the <Icon name="user-plus" />{' '}
          icon above.
        </>
      }
    />
  ) : (
    <>
      {groupedMembers.map((group) => (
        <li key={group.status}>
          <div className={styles.groupHead}>
            <Text.Paragraph
              variant="3"
              color="var(--sidebar-text-color-secondary)"
            >
              {group.status} ({group.members.length})
            </Text.Paragraph>
          </div>
          <ul className={styles.list}>
            {group.members.map((member) => (
              <PresenceListItem
                key={member.id}
                member={member}
                ownProfile={profile}
                awayStatuses={awayStatuses}
                isFavorite={favoriteAgentIds.includes(member.id)}
                onAddToFavorites={addFavorite}
                onRemoveFromFavorites={removeFavorite}
              />
            ))}
          </ul>
        </li>
      ))}
    </>
  );
};
